import React from "react";
import { Link } from "react-router-dom";
import CTABand from "@/components/CTABand";
import SectionLabel from "@/components/SectionLabel";
import Crown from "@/components/Crown";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import ASSETS from "@/lib/assets";

const PROG_MARQUEE = [
  "WHISPER", "GRIT™", "SPARKLE", "ASCEND",
  "IDENTITY RECONNECTION", "HIGH-PERFORMING WOMEN",
];

const ProgMarquee = () => {
  const items = [...PROG_MARQUEE, ...PROG_MARQUEE, ...PROG_MARQUEE, ...PROG_MARQUEE];
  return (
    <div className="marquee" data-testid="programs-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const PROGRAMS = [
  {
    id: "whisper",
    num: "01",
    name: "Whisper",
    subtitle: "Awakening · Entry Level",
    forLine: "FOR THE WOMAN WHO FEELS THE QUIET PULL",
    description:
      "Something is calling you back to yourself. You can’t quite name it, but you feel it. Whisper is where the journey begins — identity awareness, inner clarity, and the first steps toward alignment.",
    inside: [
      "6 weeks of group coaching sessions",
      "Identity awareness exercises & prompts",
      "Private community access",
      "Weekly live Q&A calls",
    ],
    forHeadline: "You’re ready to begin.",
    forBody:
      "You’ve been high-achieving your whole life, but lately something feels off. You’re not sure what you want anymore — just that you want more than this. Whisper is the first step home.",
  },
  {
    id: "grit",
    num: "02",
    name: "GRIT™",
    subtitle: "Transformation · Flagship",
    forLine: "THE IDENTITY TRANSFORMATION SYSTEM",
    description:
      "The full 12-week transformation. Grounded. Resilient. Intentional. Transformed. Where high-performing women rebuild their identity from the inside out — without losing what they’ve built.",
    inside: [
      "12 weeks of intensive group coaching",
      "Weekly 1:1 coaching check-ins",
      "Full workbook & resource library",
      "Private community & accountability partners",
      "Live hot seat coaching sessions",
    ],
    forHeadline: "You’re ready to transform.",
    forBody:
      "You’ve been performing a version of yourself for so long you’ve forgotten who you actually are. GRIT™ is for the woman done playing small — and ready to do the real work.",
  },
  {
    id: "sparkle",
    num: "03",
    name: "Sparkle",
    subtitle: "Visibility · Momentum",
    forLine: "FOR THE WOMAN READY TO BE SEEN",
    description:
      "You’ve done the inner work. Now it’s time to show up, stand out, and lead with full presence. Sparkle is about owning your voice and your power in every room you walk into.",
    inside: [
      "30-day post-program support",
      "10 weeks of visibility-focused coaching",
      "Personal brand & presence framework",
      "Communication & leadership skills",
      "Media & content confidence training",
      "Network activation strategy",
      "Live practice & feedback sessions",
    ],
    forHeadline: "You’re ready to be seen.",
    forBody:
      "You’ve done the healing. You know who you are. Now it’s time the world knew too. Sparkle is for the woman stepping into her next chapter with full confidence.",
  },
  {
    id: "ascend",
    num: "04",
    name: "Ascend",
    subtitle: "Legacy · Elite Level",
    forLine: "FOR THE WOMAN BUILDING HER LEGACY",
    description:
      "This is for the woman operating at the highest level. You’re not just building a career — you’re building a legacy. Ascend is for the woman ready to lead with her full self.",
    inside: [
      "24 weeks of elite group + 1:1 coaching",
      "Legacy & leadership framework",
      "Business & brand strategy sessions",
      "VIP mastermind access",
      "Quarterly strategy intensives",
      "Lifetime community access",
    ],
    forHeadline: "You’re ready to ascend.",
    forBody:
      "You’re already successful by every external measure. Ascend is for the woman who wants to make sure that success is actually hers — aligned, intentional, and lasting.",
  },
];

const Programs = () => (
  <div data-testid="page-programs">
    {/* HERO */}
    <section className="hero">
      <div className="hero-grid">
        <div className="hero-content">
          <Crown size={42} opacity={0.95} />
          <SectionLabel gold>GROUP COACHING PROGRAMS</SectionLabel>
          <h1>
            Every woman’s journey <em>starts somewhere.</em>
          </h1>
          <p className="hero-tagline">
            Four distinct programs designed to meet you exactly where you are —
            and take you exactly where you’re meant to go.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
            {PROGRAMS.map((p) => (
              <a key={p.id} href={`#${p.id}`} className="btn btn-ghost" style={{ padding: "12px 18px", fontSize: 11 }} data-testid={`prog-jump-${p.id}`}>
                {p.num} · {p.name}
              </a>
            ))}
          </div>
        </div>
        <div className="hero-photo">
          <PhotoPlaceholder
            src={ASSETS.photoGroup}
            position="center 40%"
            label="Nijah Almond leading a group coaching session"
          />
        </div>
      </div>
    </section>

    <ProgMarquee />

    {PROGRAMS.map((p, idx) => {
      const even = idx % 2 === 1;
      return (
        <section
          id={p.id}
          key={p.id}
          className={`prog-detail ${even ? "even" : "odd"} ${even ? "reverse" : ""} scroll-to-top-anchor`}
          data-testid={`prog-section-${p.id}`}
        >
          <div className="container-x">
            <div className="pd-grid">
              {/* LEFT: Program details */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <Crown size={36} opacity={0.85} />
                  <div className="prog-num-big">{p.num}</div>
                </div>

                <div className="eyebrow" style={{ marginTop: 18 }}>
                  {p.subtitle}
                </div>
                <h2 className="h-display" style={{
                  fontSize: "clamp(40px, 4.5vw, 64px)",
                  color: "var(--teal)",
                  margin: "8px 0 6px",
                }}>
                  {p.name}
                </h2>
                <div style={{
                  fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase",
                  color: "var(--coral)", fontWeight: 500, marginBottom: 18,
                }}>
                  {p.forLine}
                </div>
                <p className="body-base" style={{ marginBottom: 24 }}>{p.description}</p>

                <div style={{
                  borderTop: "1px solid rgba(13,31,36,.12)",
                  paddingTop: 20, marginTop: 8,
                }}>
                  <h4 className="cormorant" style={{
                    fontSize: 13, letterSpacing: 2.5, textTransform: "uppercase",
                    color: "var(--coral)", fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500, marginBottom: 14,
                  }}>
                    What’s included
                  </h4>
                  <ul className="who-card" style={{ background: "transparent", border: 0, padding: 0 }}>
                    {p.inside.map((i) => <li key={i}>{i}</li>)}
                  </ul>
                </div>

                <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
                  <Link to="/contact" className="btn btn-teal" data-testid={`prog-apply-${p.id}`}>
                    Apply Now →
                  </Link>
                  <Link to="/contact" className="btn btn-outline-dark" data-testid={`prog-learn-${p.id}`}>
                    Learn More
                  </Link>
                </div>
              </div>

              {/* RIGHT: Who this is for */}
              <div className="who-card who-card-pro">
                <div style={{
                  fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase",
                  color: "var(--coral)", fontWeight: 500, marginBottom: 12,
                }}>
                  Who this is for
                </div>
                <h4 className="cormorant" style={{
                  fontSize: 28, color: "var(--teal)", lineHeight: 1.15,
                  marginBottom: 14,
                }}>
                  {p.forHeadline}
                </h4>
                <p className="body-base" style={{ fontSize: 14 }}>
                  {p.forBody}
                </p>
                <div style={{
                  borderTop: "1px solid rgba(13,31,36,.08)",
                  marginTop: 22, paddingTop: 18,
                }}>
                  <p style={{ fontSize: 12, color: "rgba(13,31,36,.6)", lineHeight: 1.6 }}>
                    Cohorts run several times a year. Apply to be considered for the next opening.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    })}

    <CTABand
      eyebrow="NOT SURE WHERE TO START?"
      title={<>Not sure which program <span className="italic-c text-gold">is right for you?</span></>}
      sub="Submit an application and we’ll help you find your perfect starting point."
      primaryLabel="Apply Now →"
      primaryTo="/contact"
      secondaryLabel="Explore 1:1 Coaching"
      secondaryTo="/coaching"
    />
  </div>
);

export default Programs;
